import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, signal } from '@angular/core';

import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { WebhooksService } from '@generated/services/webhooks.service';
import { WebhookDto } from '@generated/models/webhook-dto';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { OAuthApplicationsComponent } from '../oauth-applications/oauth-applications.component';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-developers',
  imports: [
    AssistantSurfaceDirective,
    AssistantActionDirective,
    FormsModule,
    RouterLink,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TableModule,
    TabsModule,
    TooltipModule,
    DeleteConfirmationComponent,
    OAuthApplicationsComponent
],
  templateUrl: './developers.component.html',
  styleUrl: './developers.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DevelopersComponent implements OnInit {
  webhooks = signal<WebhookDto[]>([]);
  filteredWebhooks = signal<WebhookDto[]>([]);
  urlFilter = '';
  loading = signal(false);

  deleteDialogVisible = false;
  deleting = false;
  webhookToDelete: WebhookDto | null = null;

  readonly deleteTitle = $localize`:@@developers.delete.title:Delete Webhook`;
  readonly deleteSubtitle = $localize`:@@developers.delete.subtitle:Are you sure you want to delete this webhook? This action cannot be undone.`;

  private router = inject(Router);
  private webhooksService = inject(WebhooksService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  /** Active tab is URL-driven: /developers → 'webhooks', /developers/oauth-applications → 'oauth'. */
  activeTab = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => (e.urlAfterRedirects.endsWith('/oauth-applications') ? 'oauth' : 'webhooks') as 'webhooks' | 'oauth'),
      startWith((this.router.url.endsWith('/oauth-applications') ? 'oauth' : 'webhooks') as 'webhooks' | 'oauth'),
    ),
    { initialValue: (this.router.url.endsWith('/oauth-applications') ? 'oauth' : 'webhooks') as 'webhooks' | 'oauth' },
  );

  onTabChange(value: 'webhooks' | 'oauth'): void {
    this.router.navigate(value === 'oauth' ? ['/developers/oauth-applications'] : ['/developers']);
  }

  ngOnInit(): void {
    this.loadWebhooks();
  }

  loadWebhooks(): void {
    this.loading.set(true);
    this.webhooksService.apiWebhooksGet().subscribe({
      next: (webhooks: WebhookDto[]) => {
        this.webhooks.set(webhooks);
        this.applyFilter();
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.notificationService.showError($localize`:@@developers.notify.loadFail:Failed to load webhooks`);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  applyFilter(): void {
    const query = this.urlFilter.toLowerCase().trim();
    if (!query) {
      this.filteredWebhooks.set(this.webhooks());
    } else {
      this.filteredWebhooks.set(this.webhooks().filter(w => w.url?.toLowerCase().includes(query)));
    }
  }

  addWebhook(): void {
    this.router.navigate(['/developers/webhooks/new']);
  }

  editWebhook(webhook: WebhookDto): void {
    if (webhook.id) {
      this.router.navigate(['/developers/webhooks', webhook.id]);
    }
  }

  confirmDelete(webhook: WebhookDto, event: Event): void {
    event.stopPropagation();
    this.webhookToDelete = webhook;
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm(): void {
    if (!this.webhookToDelete?.id) return;

    this.deleting = true;
    this.cdr.markForCheck();

    this.webhooksService.apiWebhooksIdDelete({ id: this.webhookToDelete.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@developers.notify.deleted:Webhook deleted`);
        this.deleting = false;
        this.deleteDialogVisible = false;
        this.webhookToDelete = null;
        this.loadWebhooks();
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.notificationService.showError($localize`:@@developers.notify.deleteFail:Failed to delete webhook`);
        this.deleting = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.webhookToDelete = null;
    this.cdr.markForCheck();
  }
}
